import { Box, Button, Tooltip, Typography } from "@mui/material";
import { convertESTtoUserLocalDateAndTime } from "helpers/methods";
import strings from "global/constants/StringConstants";
import { SignRequest } from "models/interfaces";
import { DocumentGlyph } from "../components/SignRequestsIcons";
import inboxStyles from "./Inbox.styles";
import { InboxRowAction, InboxRowState } from "./InboxRowHelper";

const styles = inboxStyles;

interface InboxRequestRowProps {
  request: SignRequest;
  state: InboxRowState;
  disabled: boolean;
  onAction: (action: InboxRowAction, request: SignRequest) => void;
}

/**
 * One inbox row: status rail, document tile, subject with its meta line, the
 * reader-perspective status chip, and whatever single action that state offers.
 * The action slot keeps its width even when empty so every chip lines up.
 *
 * Below `sm` the reference turns each row into its own card and stacks the
 * document block over the chip / action line. `rowMain` and `rowMeta` group the
 * two halves for that, and go back to `display: contents` from `sm` up so the
 * one-line tablet and desktop layouts are byte-for-byte what they were.
 */
const InboxRequestRow = ({
  request,
  state,
  disabled,
  onAction,
}: InboxRequestRowProps) => {
  const createdOn = request.createdOn
    ? convertESTtoUserLocalDateAndTime(
        request.createdOn,
        "MMM D, h:mm A",
        "MM/DD/YYYY HH:mm:ss",
      )
    : "";

  const submeta = [request.requestedBy?.name, createdOn, state.note]
    .filter(Boolean)
    .join(" · ");

  return (
    <Box sx={styles.row}>
      <Box sx={styles.rowAccent(state.key)} />

      <Box sx={styles.rowMain}>
        <Box sx={styles.iconTile(state.key)}>
          <DocumentGlyph sx={styles.rowIcon} />
        </Box>

        <Box sx={styles.rowText}>
          <Tooltip title={request.emailSubject} arrow describeChild>
            <Typography component="div" sx={styles.rowTitle(state.muted)}>
              {request.emailSubject}
            </Typography>
          </Tooltip>
          <Typography component="div" sx={styles.rowSub}>
            {submeta}
          </Typography>
        </Box>
      </Box>

      <Box sx={styles.rowMeta}>
        <Box sx={styles.statusChip(state.key)}>
          <Box component="span" sx={styles.statusDot(state.key)} />
          <Box component="span" sx={styles.statusLabel(state.key)}>
            {state.label}
          </Box>
        </Box>

        <Box sx={styles.actionSlot}>
          {state.action && (
            <Button
              disableRipple
              disabled={disabled}
              aria-label={`${strings.inboxRowActions[state.action]} ${
                request.emailSubject
              }`}
              onClick={() => onAction(state.action!, request)}
              sx={
                state.action === "sign"
                  ? styles.rowActionPrimary
                  : styles.rowActionSecondary
              }
            >
              {/*
                Full and compact labels for the desktop vs. the narrower phone/
                tablet slot. Both are in the DOM so the breakpoint, not a
                viewport listener, picks one.
              */}
              <Box component="span" sx={styles.rowActionLabelFull}>
                {strings.inboxRowActions[state.action]}
              </Box>
              <Box component="span" sx={styles.rowActionLabelCompact}>
                {strings.inboxRowActionsCompact[state.action]}
              </Box>
            </Button>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default InboxRequestRow;
